import React, { useState } from "react";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { ReactComponent as SearchIcon } from "../../assets/icons/searchIcon.svg";
import { getTrendAnalysisColorsByMetaPostId } from "../../app/api/trendAnalysisColor";
import { RootState } from "../../app/store";
import { ITrendAnalysisColorWithDetails } from "../../interface/TrendAnalysisColorInterface";
import ColorComparisonCard from "../../components/TrendAnalysisColor/ColorComparisonCard";
import { User } from "../../types";

type Props = {
  isSidebarOpen: boolean;
};

const TrendAnalysisColors: React.FC<Props> = ({ isSidebarOpen }) => {
  const user = useSelector((state: RootState) => state.user.userInfo as User);
  const [metaPostId, setMetaPostId] = useState("");
  const [submittedMetaPostId, setSubmittedMetaPostId] = useState("");
  const [colors, setColors] = useState<ITrendAnalysisColorWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (event?: React.FormEvent) => {
    event?.preventDefault();

    const trimmedMetaPostId = metaPostId.trim();
    if (!trimmedMetaPostId) {
      toast.error("Please enter a meta post ID", { hideProgressBar: true });
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setSubmittedMetaPostId(trimmedMetaPostId);

    try {
      const response = await getTrendAnalysisColorsByMetaPostId(
        trimmedMetaPostId,
        user
      );
      setColors(response.data || []);
    } catch (error) {
      setColors([]);
      const status = (error as AxiosError).response?.status;
      if (status === 403) {
        toast.error("You do not have access to this feature", {
          hideProgressBar: true,
        });
      } else if (status !== 400) {
        toast.error("Failed to fetch trend analysis colors", {
          hideProgressBar: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`${!isSidebarOpen ? "w-[calc(100vw-265px)]" : "w-[calc(100vw-105px)]"} fixed transition-all duration-500 ease-in-out overflow-y-scroll h-[calc(100vh-80px)] ml-1 px-6 py-5`}
    >
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-primary">
            Trend Analysis Colors
          </h1>
          <p className="text-sm text-secondary mt-1">
            Compare trend analysis color data with matched pantones_new records
            for a post.
          </p>
        </div>

        <form
          onSubmit={handleSearch}
          className="bg-white rounded-2xl border border-light p-4 mb-6 flex flex-col sm:flex-row gap-3"
        >
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
            <input
              type="text"
              value={metaPostId}
              onChange={event => setMetaPostId(event.target.value)}
              placeholder="Enter meta post ID"
              className="w-full h-11 pl-10 pr-4 rounded-xl border border-light text-primary placeholder:text-tertiary focus:outline-none focus:border-default"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="h-11 px-6 rounded-xl bg-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-60"
          >
            {loading ? "Searching..." : "Search"}
          </button>
        </form>

        {loading && (
          <div className="space-y-4">
            <Skeleton height={280} borderRadius={16} />
            <Skeleton height={280} borderRadius={16} />
          </div>
        )}

        {!loading && hasSearched && colors.length === 0 && (
          <div className="bg-white rounded-2xl border border-light p-8 text-center">
            <p className="text-primary font-medium">No colors found</p>
            <p className="text-sm text-secondary mt-2">
              No trend analysis color rows exist for meta post ID{" "}
              <span className="font-medium">{submittedMetaPostId}</span>.
            </p>
          </div>
        )}

        {!loading && colors.length > 0 && (
          <div className="space-y-5">
            <p className="text-sm text-secondary">
              Showing {colors.length} result
              {colors.length === 1 ? "" : "s"} for{" "}
              <span className="font-medium text-primary">
                {submittedMetaPostId}
              </span>{" "}
              sorted by dominance.
            </p>
            {colors.map((item, index) => (
              <ColorComparisonCard key={item.id} item={item} index={index} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrendAnalysisColors;
